import type {
  EngineStatusResponse,
  NewsItemDto,
  PositionDto,
  ServerWsMessage,
  SignalDto,
} from '@et/shared';
import { create } from 'zustand';
import { api } from '../api/client';
import type { WsStatus } from '../api/ws-client';
import { t } from '../lib/i18n';

/**
 * Live state fed by REST (initial load, re-fetch on reconnect) and the WebSocket (deltas).
 * Feeds are capped so a long-open dashboard does not grow without bound.
 */

const FEED_CAP = 100;

export interface FeedEntry {
  readonly id: string;
  readonly ts: string;
  readonly kind: 'signal' | 'rejected' | 'news' | 'trade' | 'veto' | 'error' | 'breaker' | 'listing';
  readonly text: string;
  readonly tone: 'neutral' | 'good' | 'bad';
}

interface PnlLive {
  equity: string;
  dailyPnl: string;
  drawdownPct: string;
}

interface LiveState {
  ws: WsStatus;
  status: EngineStatusResponse | null;
  positions: PositionDto[];
  pnl: PnlLive | null;
  feed: FeedEntry[];
  signals: SignalDto[];
  news: NewsItemDto[];
  loadAll: () => Promise<void>;
  apply: (m: ServerWsMessage) => void;
  setWs: (s: WsStatus) => void;
}

const push = (feed: FeedEntry[], e: FeedEntry): FeedEntry[] => [e, ...feed].slice(0, FEED_CAP);

export const useLive = create<LiveState>((set, get) => ({
  ws: 'closed',
  status: null,
  positions: [],
  pnl: null,
  feed: [],
  signals: [],
  news: [],

  setWs: (ws) => {
    set({ ws });
  },

  loadAll: async () => {
    const [status, positions, signals, news] = await Promise.all([
      api.get<EngineStatusResponse>('/engine/status'),
      api.get<PositionDto[]>('/positions'),
      api.get<SignalDto[]>('/signals?limit=50'),
      api.get<NewsItemDto[]>('/news?limit=50'),
    ]);
    set({ status, positions, signals, news });
  },

  apply: (m) => {
    const s = get();
    switch (m.type) {
      case 'engine.status':
        set({ status: m.payload });
        break;
      case 'pnl.update':
        set({ pnl: m.payload });
        break;
      case 'trade.opened':
        set({
          positions: [...s.positions.filter((p) => p.tradeId !== m.payload.tradeId), m.payload],
          feed: push(s.feed, { id: `o-${m.payload.tradeId}`, ts: m.ts, kind: 'trade', text: t('Opened {symbol} × {qty} @ {price}', { symbol: m.payload.symbol, qty: m.payload.qty, price: m.payload.avgEntry }), tone: 'neutral' }),
        });
        break;
      case 'trade.closed': {
        const win = !m.payload.realizedPnl.startsWith('-');
        set({
          positions: s.positions.filter((p) => p.tradeId !== m.payload.tradeId),
          feed: push(s.feed, { id: `c-${m.payload.tradeId}`, ts: m.ts, kind: 'trade', text: t('Closed {symbol} ({reason}) {pnl} USDT', { symbol: m.payload.symbol, reason: t(m.payload.closeReason), pnl: `${win ? '+' : ''}${m.payload.realizedPnl}` }), tone: win ? 'good' : 'bad' }),
        });
        break;
      }
      case 'position.update':
        set({
          positions: s.positions.map((p) =>
            p.tradeId === m.payload.tradeId
              ? { ...p, lastPrice: m.payload.lastPrice, unrealizedPnl: m.payload.unrealizedPnl, currentStop: m.payload.currentStop, highWater: m.payload.highWater }
              : p,
          ),
        });
        break;
      case 'signal.detected':
        set({
          signals: [m.payload, ...s.signals].slice(0, FEED_CAP),
          feed: push(s.feed, { id: `s-${m.payload.id}`, ts: m.ts, kind: 'signal', text: t('{strategy} → {symbol} (confidence {conf})', { strategy: m.payload.strategyKey, symbol: m.payload.symbol, conf: m.payload.confidence }), tone: 'good' }),
        });
        break;
      case 'signal.rejected':
        set({ feed: push(s.feed, { id: `r-${m.payload.signalId}`, ts: m.ts, kind: 'rejected', text: t('Signal rejected: {reason}', { reason: m.payload.rejectionReason }), tone: 'neutral' }) });
        break;
      case 'news.item':
        set({
          news: [m.payload, ...s.news].slice(0, FEED_CAP),
          feed: push(s.feed, { id: `n-${m.payload.id}`, ts: m.ts, kind: 'news', text: `${m.payload.symbols.join(', ') || '—'} · ${m.payload.title}`, tone: 'neutral' }),
        });
        break;
      case 'symbol.veto':
        set({ feed: push(s.feed, { id: `v-${m.payload.symbol}-${m.ts}`, ts: m.ts, kind: 'veto', text: m.payload.cleared ? t('Veto cleared: {symbol}', { symbol: m.payload.symbol }) : t('VETO {symbol}: {title}', { symbol: m.payload.symbol, title: m.payload.newsTitle }), tone: m.payload.cleared ? 'neutral' : 'bad' }) });
        break;
      case 'market.listing': {
        const text = m.payload.status === 'trading'
          ? t('New listing on Binance: {symbol} is now trading', { symbol: m.payload.symbol })
          : m.payload.status === 'upcoming'
            ? t('Coming to Binance: {symbol}', { symbol: m.payload.symbol })
            : t('Removed from Binance: {symbol}', { symbol: m.payload.symbol });
        set({ feed: push(s.feed, { id: `l-${m.payload.symbol}-${m.payload.status}`, ts: m.ts, kind: 'listing', text, tone: m.payload.status === 'removed' ? 'bad' : 'good' }) });
        break;
      }
      case 'risk.circuit_breaker':
        set({ feed: push(s.feed, { id: `b-${m.ts}`, ts: m.ts, kind: 'breaker', text: m.payload.tripped ? t('Circuit breaker TRIPPED at {dd}% (limit {limit}%)', { dd: m.payload.drawdownPct, limit: m.payload.limitPct }) : t('Circuit breaker reset'), tone: m.payload.tripped ? 'bad' : 'neutral' }) });
        break;
      case 'log.error':
        set({ feed: push(s.feed, { id: `e-${m.ts}-${m.payload.module}`, ts: m.ts, kind: 'error', text: `${m.payload.module}: ${m.payload.message}`, tone: 'bad' }) });
        break;
      default:
        break;
    }
  },
}));
